import { z } from 'zod';
import { seatChangeSchema, ticketSeatSchema } from "./seat-purchase-schemas.js";
import { ticketVoidNoticeSchema } from "./refund-schemas.js";
import { moneySchema } from "./event-schemas.js";
/**
 * Checkout, order and payment-intent contracts (SDAD Sections 18, 19).
 *
 * The vocabulary is deliberately customer-facing. A person waiting for a page to
 * load has no use for `inventory_hold_id`; they need to know what they are
 * buying, what it costs, and how long they have. Internal identifiers appear
 * only where the client genuinely has to send one back.
 *
 * From Slice 4 these contracts *can* express a paid order, and every one of them
 * is careful about how. Payment state is provider-neutral (ADR 0021), payment
 * availability is stated by the server rather than guessed by a client, and the
 * organizer-facing money contract is confirmed gross sales — never a balance, a
 * payable or a payout, because no ledger exists to compute one.
 */
// --- Beginning a checkout ---------------------------------------------------
/** General admission: name a product and how many. */
export const generalAdmissionCheckoutSchema = z.object({
    ticketProductId: z.string().uuid(),
    /**
     * Bounded by a configured safety ceiling rather than a purchase-limit policy.
     * Per-account and per-event purchase limits are DropControl's problem
     * (SDAD Section 17.1) and are not decided here.
     */
    quantity: z.number().int().min(1).max(50),
});
/**
 * Reserved seating: name the hold you already took.
 *
 * A hold, and nothing else. Not the product, not the quantity, not the price —
 * all three are already written on the hold, and letting a client restate them
 * would be inviting it to disagree with the server about what it is buying.
 *
 * The identifier is not a capability. Possessing a syntactically valid hold id
 * gets a caller nowhere: the hold must be *theirs*, and one that is not is
 * refused as not-found rather than as forbidden, because confirming that
 * somebody else's hold exists is an oracle (ADR 0009).
 */
export const reservedSeatCheckoutSchema = z.object({
    seatHoldId: z.string().uuid(),
});
/** The general-admission fields, named once so the exclusivity rule can cite them. */
const GENERAL_ADMISSION_FIELDS = ['ticketProductId', 'quantity'];
/**
 * Beginning a checkout — one intent per request, and the caller must say which.
 *
 * The two shapes are **mutually exclusive**. A body carrying a seat hold *and*
 * a product or quantity is refused as malformed, not silently resolved in
 * favour of one of them.
 *
 * An earlier version preferred the hold and dropped the quantity. There was no
 * authority problem in that — the hold already knows how many chairs it has,
 * and nothing commercial came from the client either way — but it made a client
 * defect invisible. A page that sent both because it had not finished switching
 * modes would have been quietly served the right answer, right up until the day
 * the two disagreed about something that mattered. An API that accepts two
 * conflicting statements of intent is an API that cannot tell its callers they
 * are confused.
 *
 * So the ambiguity is a 400 with both offending fields named, and the caller
 * decides which checkout it is asking for.
 */
export const beginCheckoutRequestSchema = z
    .looseObject({})
    .superRefine((body, ctx) => {
    const record = body;
    if (record.seatHoldId === undefined)
        return;
    const alsoNamed = GENERAL_ADMISSION_FIELDS.filter((field) => record[field] !== undefined);
    for (const field of alsoNamed) {
        ctx.addIssue({
            code: 'custom',
            path: [field],
            message: 'A reserved-seat checkout names only the hold. Send seatHoldId on its own, or send ticketProductId and quantity without it.',
        });
    }
})
    // Piped rather than unioned directly, so the exclusivity rule runs *before*
    // either branch gets a chance to match and quietly strip the other's fields.
    .pipe(z.union([reservedSeatCheckoutSchema, generalAdmissionCheckoutSchema]));
/** True when this request adopts an existing reserved-seat hold. */
export function isReservedSeatCheckout(request) {
    return 'seatHoldId' in request;
}
/** The commercial terms a customer accepted, frozen at quote time. */
export const acceptedTermsSchema = z.object({
    displayName: z.string(),
    description: z.string().nullable(),
    accessScope: z.enum(['ALL_SESSIONS', 'SPECIFIC_SESSIONS']),
    sessionIds: z.array(z.string().uuid()),
    /**
     * Which credential this ticket will use. Disclosed *here* rather than on the
     * public event card: at checkout it changes what the buyer must bring, which
     * is exactly when they need to know (ADR 0016).
     */
    securityMode: z.enum(['STANDARD_PASS', 'SECURE_PASS']),
    ticketProductVersion: z.number().int(),
});
export const checkoutQuoteLineSchema = z.object({
    ticketProductId: z.string().uuid(),
    quantity: z.number().int(),
    unitPrice: moneySchema,
    lineSubtotal: moneySchema,
    terms: acceptedTermsSchema,
});
export const checkoutQuoteSchema = z.object({
    checkoutQuoteId: z.string().uuid(),
    currency: z.string(),
    lines: z.array(checkoutQuoteLineSchema),
    subtotal: moneySchema,
    /**
     * Present and zero. No fee schedule, tax treatment or discount policy has been
     * approved (SDAD Section 72), and inventing percentages to fill a checkout
     * screen would be fabricating commercial policy. The shape carries them so a
     * later approved policy needs no rewrite of historical quotes.
     */
    discount: moneySchema,
    fees: moneySchema,
    tax: moneySchema,
    total: moneySchema,
    /** Server-authoritative. A client displays this; it never decides it. */
    holdExpiresAt: z.string(),
    createdAt: z.string(),
});
export const orderStatusSchema = z.enum([
    'DRAFT',
    'QUOTED',
    'INVENTORY_HELD',
    'PAYMENT_PENDING',
    'EXPIRED',
    'PAYMENT_FAILED',
    'CANCELLED',
    'PAID_PENDING_ISSUANCE',
    /**
     * The free counterpart of the state above it: the customer has done
     * everything asked of them, and what was asked was nothing. Distinct from
     * `PAID_PENDING_ISSUANCE` on purpose — reading "paid" on an order where no
     * money moved would make every downstream report quietly wrong.
     */
    'ZERO_TOTAL_PENDING_ISSUANCE',
    'TICKETS_ISSUED',
    'COMPLETED',
]);
/**
 * Where a payment attempt has got to, in GateCheckPro's own words.
 *
 * Provider-neutral (ADR 0021). No surface has ever seen a Flutterwave status
 * string and none ever will — a client that learned to branch on one would be a
 * client that breaks when a second adapter arrives.
 */
export const paymentAttemptStatusSchema = z.enum([
    'CREATED',
    'PROVIDER_PENDING',
    'REQUIRES_CUSTOMER_ACTION',
    /** We are asking the provider. Deliberately distinct from "failed". */
    'VERIFYING',
    'SUCCEEDED',
    'FAILED',
    'EXPIRED',
    /** Something did not add up and a person has to look (ADR 0023). */
    'REVIEW_REQUIRED',
]);
/**
 * Which rail a payment is being collected through.
 *
 * Named for what a Nigerian customer recognises, not for a Flutterwave product,
 * so a second adapter offering the same rails needs no new value. Card is
 * absent by ruling: the documented v4 card flow requires the merchant to collect
 * and encrypt the PAN, and GateCheckPro does not take cardholder data into
 * scope in R1.
 */
export const paymentRailSchema = z.enum(['USSD', 'BANK_TRANSFER']);
export const PAYMENT_RAIL_LABELS = Object.freeze({
    USSD: 'Bank USSD',
    BANK_TRANSFER: 'Bank transfer',
});
/**
 * What the customer has to do next — a tagged union, not a URL.
 *
 * The first cut of this contract was a nullable `customerActionUrl`, which
 * assumed every payment ends in a redirect. Neither rail GateCheckPro ships
 * does. USSD returns a code to dial; a bank transfer returns a temporary
 * account to pay into and a deadline. A surface reading a URL field would have
 * shown an empty screen to a customer waiting to be told what to do.
 *
 * Provider-specific `next_action` structures stay inside the adapter. This is
 * what every client renders.
 */
export const customerActionSchema = z.discriminatedUnion('type', [
    /** Nothing for the customer to do; the server is waiting on the provider. */
    z.object({ type: z.literal('NONE') }),
    /**
     * Send the customer somewhere. Unused by either R1 rail, and carried so a
     * later hosted-checkout flow is an addition rather than a contract change.
     *
     * Coming *back* from it proves nothing: the return route is UX, and the
     * server decides what happened (SDAD Section 19.1).
     */
    z.object({ type: z.literal('REDIRECT'), url: z.string() }),
    /**
     * Do something on your phone — dial a USSD code, approve in a banking app.
     *
     * `instruction` is the provider's own words, shown verbatim. Paraphrasing a
     * dial string is how a customer ends up dialling something that does not
     * work.
     */
    z.object({
        type: z.literal('PAYMENT_INSTRUCTION'),
        instruction: z.string(),
    }),
    /**
     * Transfer to a temporary account.
     *
     * `amount` is what the customer is told to send, which is not assumed equal
     * to what they were quoted — the server states both, and any difference is an
     * exception rather than something a surface silently reconciles.
     *
     * The account is transaction infrastructure with a deadline, not a customer's
     * banking identity, and the surfaces say so rather than inviting anyone to
     * save it.
     */
    z.object({
        type: z.literal('BANK_TRANSFER_DETAILS'),
        bankName: z.string(),
        accountNumber: z.string(),
        amount: moneySchema,
        expiresAt: z.string().nullable(),
    }),
]);
export const paymentAttemptSchema = z.object({
    paymentAttemptId: z.string().uuid(),
    status: paymentAttemptStatusSchema,
    rail: paymentRailSchema,
    /** What the customer must do now. Never null — `NONE` is the empty case. */
    customerAction: customerActionSchema,
});
export const initiatePaymentRequestSchema = z.object({
    /** Which rail the customer chose. The server re-checks that it is offered. */
    rail: paymentRailSchema,
});
/**
 * A real obligation to collect money, or nothing at all.
 *
 * This object describes a PaymentIntent. It is therefore absent — `null` on the
 * order — for an order that has none, which is every zero-total order. It
 * carries no value meaning "there isn't one": a field named `paymentIntentId`
 * holding null, or a status meaning "not required", would be this object
 * claiming to describe something that does not exist. Whether an order owes
 * anything is `Order.paymentRequirement`, and the sentence a customer reads is
 * `Order.paymentMessage`; both are facts about the order, and both are present
 * whether or not there is anything to pay.
 */
export const paymentIntentSchema = z.object({
    paymentIntentId: z.string().uuid(),
    expectedAmount: moneySchema,
    status: z.enum(['AWAITING_PAYMENT', 'EXPIRED', 'FAILED', 'CANCELLED', 'SUCCEEDED']),
    expiresAt: z.string(),
    /**
     * Whether a customer could actually pay this right now.
     *
     * Stated by the server rather than inferred by each client. A surface that
     * guessed would eventually guess wrong and show somebody a payment button that
     * goes nowhere — and `false` is a truthful state here, not a broken one: a
     * deployment with no collection adapter says so.
     */
    paymentProviderConnected: z.boolean(),
    /**
     * Whether GateCheckPro is willing to take money for this order at all.
     *
     * False when the order contains a security mode the platform cannot currently
     * fulfil. Separate from provider availability because the two have different
     * causes and different remedies, and a customer deserves to be told which
     * (SDAD Section 54).
     */
    fulfillable: z.boolean(),
    /** The live attempt, if one exists. */
    attempt: paymentAttemptSchema.nullable(),
    /**
     * Rails a customer may choose between right now.
     *
     * Stated by the server, so a surface never offers a rail this deployment
     * cannot actually collect through. Empty means payment is unavailable, and
     * `message` says why.
     */
    availableRails: z.array(paymentRailSchema),
});
/**
 * Whether this order has anything to pay.
 *
 * Stated on the order rather than inferred from the absence of an intent, so a
 * surface asks the question it actually means. `NOT_REQUIRED` is not a payment
 * that succeeded, was waived, or is pending — it is an order that never owed
 * anything, because the organizer published the ticket at zero.
 */
export const paymentRequirementSchema = z.enum(['REQUIRED', 'NOT_REQUIRED']);
export const orderSchema = z.object({
    orderId: z.string().uuid(),
    orderNumber: z.string(),
    status: orderStatusSchema,
    amount: moneySchema,
    createdAt: z.string(),
    event: z.object({
        slug: z.string(),
        title: z.string(),
        timezone: z.string(),
        venueName: z.string().nullable(),
        /** The sessions this order's tickets admit to. */
        sessions: z.array(z.object({
            sessionId: z.string().uuid(),
            name: z.string().nullable(),
            startsAt: z.string(),
        })),
    }),
    quote: checkoutQuoteSchema,
    paymentRequirement: paymentRequirementSchema,
    /**
     * Plain language for where this order's payment stands, including why it
     * cannot proceed, or that there was never anything to pay.
     *
     * On the order rather than on the intent, because every order has a payment
     * situation worth a sentence and only some orders have an intent. It also
     * keeps three surfaces from drifting into three accounts of one situation.
     */
    paymentMessage: z.string(),
    /** The obligation to collect, or null when the order owes nothing. */
    paymentIntent: paymentIntentSchema.nullable(),
    /**
     * When the reservation lapses. Null once the order is no longer holding
     * inventory, so a surface never counts down to a deadline that has stopped
     * mattering.
     */
    holdExpiresAt: z.string().nullable(),
});
export const beginCheckoutResponseSchema = orderSchema;
export const orderSummarySchema = z.object({
    orderId: z.string().uuid(),
    orderNumber: z.string(),
    status: orderStatusSchema,
    amount: moneySchema,
    createdAt: z.string(),
    eventTitle: z.string(),
    eventSlug: z.string(),
    holdExpiresAt: z.string().nullable(),
});
export const orderListSchema = z.object({
    orders: z.array(orderSummarySchema),
});
export const abandonCheckoutRequestSchema = z.object({
    reasonCode: z.string().trim().min(2).max(64).default('CUSTOMER_ABANDONED'),
});
// --- Organizer-facing commerce visibility -----------------------------------
/**
 * What an organizer can see about demand.
 *
 * Deliberately not called sales or revenue. Nothing here has been paid, and a
 * dashboard that totalled payment-pending orders into a revenue figure would be
 * the first lie in the financial chain.
 */
export const eventDemandSchema = z.object({
    configuredCapacity: z.number().int(),
    saleableAllocated: z.number().int(),
    activeHolds: z.number().int(),
    availableNow: z.number().int(),
    paymentPendingOrders: z.number().int(),
    paymentPendingAmount: moneySchema.nullable(),
    /** Restated on the contract so no surface has to remember it. */
    note: z.string(),
});
// --- Tickets and passes -----------------------------------------------------
/**
 * A ticket a customer owns (SDAD Sections 20, 21).
 *
 * One per unit of admission. Four tickets is four of these, because each is
 * independently admitted and — from a later slice — independently transferable.
 */
export const ticketSchema = z.object({
    ticketId: z.string().uuid(),
    /** Readable, and not a credential. Reading it aloud admits nobody. */
    ticketNumber: z.string(),
    status: z.enum(['ISSUED', 'VOID']),
    /** Frozen at issuance; a later product revision cannot change it. */
    securityMode: z.enum(['STANDARD_PASS', 'SECURE_PASS']),
    ticketName: z.string(),
    issuedAt: z.string(),
    event: z.object({
        slug: z.string(),
        title: z.string(),
        timezone: z.string(),
        venueName: z.string().nullable(),
        startsAt: z.string().nullable(),
    }),
    orderNumber: z.string(),
    /**
     * Where this ticket sits **now**. Null for general admission.
     *
     * The current effective seat, not the chair originally bought. Those are the
     * same until an organizer authorises a move, and this field follows the move
     * — a customer standing in a venue needs to be told where to go.
     *
     * What it is *not* is a record of the purchase. `originalSeat` below is that,
     * and `seatChange` says whether the two have diverged. A surface showing this
     * field alone after a move would be telling somebody they bought a chair they
     * did not.
     *
     * Named `seat` rather than `currentSeat` because it predates the distinction
     * and renaming it would break every existing reader for no gain.
     */
    seat: ticketSeatSchema.nullable().default(null),
    /**
     * The chair this ticket was sold in, from the immutable assignment.
     *
     * Equal to `seat` until a move happens, and unchanged by everything else: a
     * later map version renumbering the venue, a reprice, a superseded
     * configuration. A customer who bought Row 12 seat 4 is still told they
     * bought Row 12 seat 4, however many times they have since been moved.
     */
    originalSeat: ticketSeatSchema.nullable().default(null),
    /**
     * Whether the seat has been moved, and a summary of it. Null when it has not.
     *
     * Null is meaningfully different from a zero count: it says the customer
     * still sits where they bought, rather than that they were moved and the
     * history is missing.
     */
    seatChange: seatChangeSchema.nullable().default(null),
    /**
     * Why this ticket no longer works, when it no longer works. Null otherwise.
     *
     * Carries the *customer-safe* reason, not the organizer's. An attendee should
     * not read `FRAUD` or `OPERATIONAL_ERROR` off their own ticket: the first is
     * an accusation and the second an internal admission, and neither is the
     * platform's to publish on an organizer's behalf. Both collapse to
     * `ORGANIZER_ACTION`, which is true.
     *
     * Null rather than an object with a null reason, so a surface cannot render
     * "this ticket was voided because null" for a perfectly valid ticket.
     */
    voidNotice: ticketVoidNoticeSchema.nullable().default(null),
});
/**
 * The pass itself.
 *
 * `pass` is the signed string a gate reads. It is returned only to the ticket's
 * owner, and the surfaces that show it say plainly what it is and is not:
 * a screenshot of it works exactly as well as the original, which is why
 * duplicate *use* is caught at the gate rather than by the credential
 * (ADR 0022).
 */
export const ticketCredentialSchema = z.object({
    credentialId: z.string(),
    format: z.literal('GCP1'),
    pass: z.string(),
    issuedAt: z.string(),
    expiresAt: z.string(),
    /** Restated on the contract so no surface has to remember to say it. */
    honesty: z.string(),
});
export const ticketListSchema = z.object({
    /**
     * `AVAILABLE` means ticket ownership works. An empty list then truthfully
     * means "you have none" rather than "this feature does not exist".
     */
    state: z.enum(['AVAILABLE', 'NOT_YET_AVAILABLE']),
    tickets: z.array(ticketSchema),
});
// --- Operations: payment exceptions -----------------------------------------
/**
 * A payment GateCheckPro could not resolve on its own (ADR 0023).
 *
 * Note the absence of any "mark as paid" action in this contract. An operator
 * can order a re-verification against the provider or record that a case was
 * settled outside the system; neither manufactures payment authority from a
 * human's opinion.
 */
export const paymentExceptionSchema = z.object({
    paymentExceptionId: z.string().uuid(),
    kind: z.enum([
        'AMOUNT_MISMATCH',
        'CURRENCY_MISMATCH',
        'REFERENCE_MISMATCH',
        'CUSTOMER_MISMATCH',
        'PAID_AFTER_INVENTORY_LOST',
        'ISSUANCE_FAILED',
        'UNRECOGNISED_TRANSACTION',
    ]),
    status: z.enum(['OPEN', 'RESOLVED']),
    openedAt: z.string(),
    organizationName: z.string(),
    orderNumber: z.string(),
    expectedAmount: moneySchema,
    /** What we expected and what we observed, so nobody reconstructs it from logs. */
    detail: z.record(z.string(), z.unknown()),
});
export const paymentExceptionListSchema = z.object({
    exceptions: z.array(paymentExceptionSchema),
});
export const resolvePaymentExceptionRequestSchema = z.object({
    /**
     * Re-verification asks the provider again and lets the ordinary verified path
     * decide. The other two record a human's finding without creating payment.
     */
    resolution: z.enum(['RESOLVED_BY_REVERIFICATION', 'RESOLVED_EXTERNALLY', 'NO_ACTION_REQUIRED']),
    note: z.string().trim().min(3).max(500),
});
// --- Organizer: provider-confirmed sales ------------------------------------
/**
 * What an organizer may be shown about money (SDAD Section 71).
 *
 * Provider-confirmed gross ticket sales, and nothing that sounds like a balance.
 * There is no ledger, no settlement engine and no payout schedule in this
 * release, so the words "balance", "payable", "settlement due", "available
 * payout" and "net revenue" must not appear on any surface — each would state a
 * financial position GateCheckPro cannot currently compute.
 */
export const eventSalesSchema = z.object({
    ticketsSold: z.number().int(),
    ticketsIssued: z.number().int(),
    /** Gross, provider-confirmed. Not a balance and not a payout. */
    confirmedGross: moneySchema.nullable(),
    ordersAwaitingIssuance: z.number().int(),
    /** Restated on the contract so no surface has to remember it. */
    note: z.string(),
});
//# sourceMappingURL=checkout-schemas.js.map